import { useEffect, useRef, useState } from 'react';
import { Box, Theme, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  authSelector,
  collaboratorSelector,
  intermitenceSelector,
  memorySelector,
  storySelector,
} from '@/store/selectors';
import { MemoriesForm } from './components';
import { UseFirstRender } from '@/hooks';
import { useAuth } from '@/hooks/authContext';
import { useRouter } from 'next/router';
import {
  actualStory,
  clearPendingInvite,
  createMemoryViewG,
  getMemories,
  inviteAccepted,
  showActualSection,
} from '@/store/actions';
import Image from 'next/image';
import { cdn_url, hasStoryRole } from '@/utils';
import { ErrorPopup } from '@/components/ErrorPopup';

export const CreateMemory = () => {
  const dispatch = useDispatch();
  const createData = useSelector(memorySelector);
  const { memoriesLoaded } = useSelector(memorySelector);
  const { story } = useSelector(storySelector);
  const { separation } = useSelector(intermitenceSelector);
  const { inviteData, inviteflag } = useSelector(collaboratorSelector);
  const { user, isAuth } = useSelector(authSelector);
  const { createMemoryStep } = createData;
  const router = useRouter();
  const { setRedirectTo } = useAuth();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [mainStep, SetMainStep] = useState(1);
  const inviteDispatched = useRef(false);

  const showScreen = () => {
    switch (createMemoryStep) {
      default:
        return <MemoriesForm SetMainStep={SetMainStep} />;
    }
  };

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (!router.isReady || isAuth) return;
    setRedirectTo(router.asPath);
    router.push('/app/register');
  }, [isAuth, router.asPath, router.isReady, setRedirectTo]);

  useEffect(() => {
    setMounted(true);
    // Reset scroll position when component mounts
    window.scrollTo(0, 0);
    const scrollableElements = document.querySelectorAll('[style*="overflow"]');
    scrollableElements.forEach((el: any) => {
      if (el.scrollTop) el.scrollTop = 0;
    });
  }, []);
  useEffect(() => {
    if (!isAuth || !story?.id || inviteDispatched.current) return;
    const isMember = hasStoryRole(user, story.id);
    if (!isMember) {
      dispatch(
        inviteAccepted({
          story_id: story.id,
          user_id: user.id,
          user_type: 'friend',
          role_name: 'Story_Collaborator',
          inviteData: inviteData ? true : false,
        }),
      );
      inviteDispatched.current = true;
      dispatch(clearPendingInvite());
    }
  }, [isAuth, story?.id, user?.id, user?.roles, dispatch]);

  UseFirstRender(() => {
    if (!story?.id) dispatch(actualStory(router.query?.id as string));
  }, [router?.query?.id]);

  UseFirstRender(() => {
    if (user) dispatch(createMemoryViewG(user?.id));
  }, [dispatch]);

  UseFirstRender(() => {
    if (window?.location && story?.id && user?.id) {
      dispatch(showActualSection(story?.title));
      dispatch(getMemories({ story_id: story?.id, publicAccess: false }));
    }
  }, [story]);

  const useBodyBackground = (color: string = '#fff') => {
    useEffect(() => {
      document.body.style.background = color;

      return () => {
        document.body.style.background = '';
      };
    }, [color]);
  };
  useBodyBackground('rgba(33, 45, 109, 1)');

  if (!mounted)
    return (
      <Box width={'100%'} height={'80vh'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
        <Image src='/images/Loading.svg' quality={'100'} alt='loading' fill style={{ objectFit: 'cover' }} />
      </Box>
    );

  return (
    <Box
      display='flex'
      flexDirection='column'
      width='100%'
      height={isMobile ? 'auto' : '100vh'}
      minHeight={isMobile ? '100vh' : 'auto'}
      position='relative'
      sx={{
        width: '100%',
        position: 'relative',
        overflow: isMobile ? 'visible' : 'hidden',
        backgroundImage: !isMobile ? 'url(/images/bg-main.jpg)' : 'url(/images/bg-main.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'bottom center',
      }}>
      {isMobile && (
        <Image
          priority
          src={`${cdn_url}${story.cover_image}`}
          fill={true}
          alt='hello'
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,

            transition: 'opacity 0.2s ease-in-out',
          }}
        />
      )}
      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          backdropFilter: 'blur(40px)',
          zIndex: 0,
        }}
      />
      <Box
        display={'flex'}
        width={'100%'}
        justifyContent={'space-between'}
        flexDirection={'column'}
        height={isMobile ? 'auto' : '100%'}
        alignItems={'center'}
        sx={{
          overflowY: isMobile ? 'visible' : 'auto',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
        }}>
        {showScreen()}
      </Box>
      <ErrorPopup />
    </Box>
  );
};
